// Daily streak state + claim. Server-authoritative via `claim_daily_checkin` RPC.
// v2: no credits — grants free score tokens + featured/custom passes.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface StreakState {
  currentStreak: number;
  bestStreak: number;
  lastCheckIn: string | null; // YYYY-MM-DD UTC
  canClaim: boolean;
}

export interface ClaimResult {
  ok: boolean;
  alreadyClaimed?: boolean;
  streak?: number;
  scoreTokens?: number;
  featuredPasses?: number;
  customFeaturedPasses?: number;
  bonusKind?: string | null;
}

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

interface ClaimRow {
  already_claimed: boolean;
  current_streak: number;
  best_streak: number;
  score_tokens: number;
  featured_passes: number;
  custom_featured_passes: number;
  tokens_expire_at: string | null;
  bonus_kind: string | null;
}

export function useDailyStreak() {
  const { user } = useAuth();
  const [state, setState] = useState<StreakState>({
    currentStreak: 0,
    bestStreak: 0,
    lastCheckIn: null,
    canClaim: true,
  });
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("user_streaks")
      .select("current_streak,best_streak,last_check_in_date")
      .eq("user_id", user.id)
      .maybeSingle();
    const last = data?.last_check_in_date ?? null;
    setState({
      currentStreak: data?.current_streak ?? 0,
      bestStreak: data?.best_streak ?? 0,
      lastCheckIn: last,
      canClaim: last !== todayUtc(),
    });
    setLoading(false);
  }, [user]);

  useEffect(() => { void reload(); }, [reload]);

  // Recover from stale state when the tab regains focus (e.g. after a claim
  // round-trip that didn't update local state correctly).
  useEffect(() => {
    const onFocus = () => { void reload(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [reload]);

  const claim = useCallback(async (): Promise<ClaimResult> => {
    if (!user) return { ok: false };
    setClaiming(true);
    const { data, error } = await supabase.rpc("claim_daily_checkin");
    setClaiming(false);
    if (error) return { ok: false };
    const row = ((data ?? []) as unknown as ClaimRow[])[0];
    if (!row) return { ok: false };
    setState((s) => ({
      ...s,
      currentStreak: row.current_streak,
      // Defensive: never let Best regress below current/previous value.
      bestStreak: Math.max(s.bestStreak, row.best_streak ?? 0, row.current_streak ?? 0),
      lastCheckIn: todayUtc(),
      canClaim: false,
    }));
    // Refetch authoritative values from user_streaks so the card always
    // reflects DB truth, not just the optimistic RPC row.
    void reload();
    return {
      ok: true,
      alreadyClaimed: row.already_claimed,
      streak: row.current_streak,
      scoreTokens: row.score_tokens,
      featuredPasses: row.featured_passes,
      customFeaturedPasses: row.custom_featured_passes,
      bonusKind: row.bonus_kind,
    };
  }, [user, reload]);

  return { state, loading, claiming, reload, claim };
}
